import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddCashArgs {
  branchId:    string;
  amount:      number;
  collectedBy: string;
  date:        string;
  remark?:     string;
}

interface GetCashHistoryParams {
  branchId:   string;
  startDate?: string;
  endDate?:   string;
  page?:      number;
  limit?:     number;
}

interface GetCashReportsParams {
  branchId?:  string;
  startDate?: string;
  endDate?:   string;
}

type CashSummaryFilter = 'today' | 'monthly' | 'last_3_month' | 'last_6_month' | 'last_12_month';

interface GetCashSummaryReportParams {
  branchId?: string;
  filter:    CashSummaryFilter;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const cashApi = createApi({
  reducerPath: 'cashApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Cash'] as const,
  endpoints:   (builder) => ({
    addCash: builder.mutation<unknown, AddCashArgs>({
      query: (body) => ({ url: `/cash`, method: 'POST', body }),
      invalidatesTags: ['Cash'],
    }),
    getCashHistory: builder.query<unknown, GetCashHistoryParams>({
      query: ({ branchId, startDate, endDate, page, limit }) => {
        const qp = new URLSearchParams();
        if (startDate)     qp.append('startDate', startDate);
        if (endDate)       qp.append('endDate', endDate);
        if (page  != null) qp.append('page', String(page));
        if (limit != null) qp.append('limit', String(limit));
        const qs = qp.toString();
        return { url: qs ? `/cash/${branchId}/history?${qs}` : `/cash/${branchId}/history`, method: 'GET' };
      },
      providesTags: ['Cash'],
    }),
    getCashReports: builder.query<unknown, GetCashReportsParams | void>({
      query: (params) => {
        const { branchId, startDate, endDate } = params || {};
        const qp = new URLSearchParams();
        if (branchId)  qp.append('branchId', branchId);
        if (startDate) qp.append('startDate', startDate);
        if (endDate)   qp.append('endDate', endDate);
        const qs = qp.toString();
        return { url: qs ? `/cash/reports?${qs}` : `/cash/reports`, method: 'GET' };
      },
      providesTags: ['Cash'],
    }),
    getCashSummaryReport: builder.query<unknown, GetCashSummaryReportParams>({
      query: ({ branchId, filter }) => {
        const qp = new URLSearchParams();
        if (branchId) qp.append('branchId', branchId);
        qp.append('filter', filter);
        return { url: `/cash/summary-report?${qp.toString()}`, method: 'GET' };
      },
      providesTags: ['Cash'],
    }),
  }),
});

export const {
  useAddCashMutation,
  useGetCashHistoryQuery,
  useGetCashReportsQuery,
  useGetCashSummaryReportQuery,
} = cashApi;
